import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Heart, MessageCircle, Minus, Plus, ShoppingCart, CalendarClock, Truck, Wallet, Expand } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { rp, fmtDateTime, dpLabel, waLink, waContacts } from "@/lib/format";
import { useShop, useSettings, useSeo, lineMax } from "@/lib/shop";
import { ProductGrid } from "@/components/site/ProductCard";
import { Loading } from "@/components/site/States";
import { PoBadge, Pill, DemoBadge, CondBadges } from "@/components/site/Badges";
import { PoTermsBlock } from "@/pages/InfoPages";
import NotFound from "@/pages/NotFound";
import { Lightbox } from "@/components/site/Lightbox";

function Gallery({ images, name }) {
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);
  const imgs = images?.length ? images : [""];
  const hasPhoto = !!images?.length;
  return (
    <div>
      <button type="button" onClick={() => hasPhoto && setOpen(true)} disabled={!hasPhoto} data-testid="gallery-open-lightbox" aria-label="Lihat foto ukuran penuh" className="group relative block w-full aspect-square rounded-2xl overflow-hidden border-2 border-neutral-900 bg-white cursor-zoom-in disabled:cursor-default">
        <img data-testid="gallery-main-image" src={imgUrl(imgs[i])} alt={name} className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        {hasPhoto && <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 border-2 border-neutral-900 px-3 py-1 text-xs font-bold"><Expand className="h-3.5 w-3.5" />Lihat penuh</span>}
      </button>
      {open && <Lightbox images={imgs} index={i} name={name} onIndex={setI} onClose={() => setOpen(false)} />}
      {imgs.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
          {imgs.map((u, k) => (
            <button key={k} data-testid={`gallery-thumb-${k}`} onClick={() => setI(k)} className={`h-20 w-20 shrink-0 rounded-lg overflow-hidden border-2 ${k === i ? "border-[var(--bk-orange)]" : "border-neutral-200"}`}>
              <img src={imgUrl(u)} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Row = ({ label, children, testid }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-dashed border-neutral-200 last:border-0 text-sm">
    <span className="text-neutral-600">{label}</span>
    <span className="font-semibold text-right" data-testid={testid}>{children}</span>
  </div>
);

function PoPanel({ po }) {
  return (
    <div className="rounded-2xl border-2 border-neutral-900 bg-[#fff7d6] p-4 sm:p-5" data-testid="po-info-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-bold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[var(--bk-orange)]" />Informasi Pre-Order</h3>
        <PoBadge status={po.status} />
      </div>
      <Row label="Harga PO" testid="po-price">{rp(po.price)}</Row>
      {po.normal_price && <Row label="Harga normal">{rp(po.normal_price)}</Row>}
      <Row label="Ketentuan DP">{dpLabel(po)}{po.dp_required ? " (wajib)" : po.dp_type !== "none" ? " (opsional)" : ""}</Row>
      <Row label="DP per item" testid="po-dp">{rp(po.dp_per_unit)}</Row>
      <Row label="Sisa pelunasan per item" testid="po-remaining">{rp(po.remaining_per_unit)}</Row>
      <Row label="Mulai PO">{fmtDateTime(po.start_at)}</Row>
      <Row label="Tutup PO" testid="po-close">{fmtDateTime(po.close_at)}</Row>
      <Row label="Estimasi kedatangan">{po.eta || "Menyusul"}</Row>
      {po.max_per_order && <Row label="Maks. per pesanan">{po.max_per_order} pcs</Row>}
      {po.quota_left !== null && <Row label="Sisa kuota">{po.quota_left} pcs</Row>}
      {po.settlement_schedule && <Row label="Jadwal pelunasan">{po.settlement_schedule}</Row>}
      {po.payment_terms && <p className="text-sm mt-3 whitespace-pre-line text-neutral-700">{po.payment_terms}</p>}
      <p className="text-[11.5px] text-neutral-600 mt-3">Estimasi kedatangan dapat berubah mengikuti jadwal supplier. Waktu dalam WIB (Asia/Jakarta).</p>
    </div>
  );
}

export default function ProductDetail({ poMode = false }) {
  const { slug } = useParams();
  const { addToCart, toggleFav, isFav } = useShop();
  const s = useSettings();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["product", slug], queryFn: () => api.get(`/products/${slug}`).then((r) => r.data), retry: false });
  const [mode, setMode] = useState(poMode ? "po" : "ready");
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(1);
  useEffect(() => {
    if (!p) return;
    if (p.sale_type === "po") setMode("po");
    else if (p.sale_type === "ready") setMode("ready");
    else setMode(poMode || !p.ready_orderable ? "po" : "ready");
    setVariant("");
    setQty(1);
  }, [p, poMode]);
  useSeo(p?.name, p ? `${p.name} (${p.sku}) — ${p.brand}. ${(p.description || "").slice(0, 120)}` : undefined);

  if (isLoading) return <Loading />;
  if (isError || !p) return <NotFound />;
  const hasVariants = p.variants?.length > 0;
  const max = mode === "ready" && hasVariants && !variant ? 0 : lineMax(p, mode, variant);
  const orderable = mode === "po" ? p.po_info?.orderable : p.ready_orderable && max > 0;
  const needVariant = mode === "ready" && hasVariants && !variant;
  const pr = p.pricing;
  const fav = isFav(p.id);
  const waText = `Halo ${s.store_name}, saya ingin bertanya tentang produk:\n${p.name}\nSKU: ${p.sku}${mode === "po" ? " (Pre-Order)" : ""}\n${window.location.href}`;

  return (
    <div className="container-x py-8" data-testid="product-detail-page">
      <nav className="text-xs text-neutral-500 mb-5">
        <Link to="/" className="hover:underline">Beranda</Link> / {poMode ? <Link to="/pre-order" className="hover:underline">Pre-Order Corner</Link> : <Link to="/katalog" className="hover:underline">Katalog</Link>}
        {p.category && <> / <Link to={`/kategori/${p.category.slug}`} className="hover:underline">{p.category.name}</Link></>}
      </nav>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        <Gallery images={p.images} name={p.name} />
        <div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {mode === "po" ? <PoBadge status={p.po_info?.status} /> : p.stock > 0 ? <Pill cls="bg-[var(--bk-yellow)] border border-neutral-900">Ready Stock</Pill> : <Pill cls="bg-neutral-800 text-white">Stok Habis</Pill>}
            {mode === "ready" && pr.discount_pct > 0 && <Pill cls="bg-[var(--bk-red)] text-white">Hemat {pr.discount_pct}%</Pill>}
            {p.is_demo && <DemoBadge />}
            <CondBadges p={p} />
          </div>
          {p.ownership === "consignment" && <p className="text-xs text-[var(--bk-blue)] font-semibold mb-2" data-testid="consignment-note">Barang titipan kolektor (consignment), dijual melalui BengkelHobby. Kondisi: {p.condition === "new" ? "Baru" : "Pre-Owned (bekas)"}.</p>}
          {p.ownership !== "consignment" && p.condition === "preowned" && <p className="text-xs font-semibold mb-2" data-testid="preowned-note">Kondisi: Pre-Owned (bekas) — cek deskripsi untuk detail kondisi.</p>}
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--bk-blue)]">{p.brand}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold leading-tight mt-1" data-testid="product-name">{p.name}</h1>
          <div className="text-sm text-neutral-500 mt-2">SKU: <span data-testid="product-sku" className="font-mono">{p.sku}</span>{p.category && <> · {p.category.name}</>}</div>

          {p.sale_type === "both" && (
            <div className="mt-5 inline-flex rounded-full border-2 border-neutral-900 p-1 bg-white" data-testid="mode-toggle">
              {[["ready", "Ready Stock"], ["po", "Pre-Order"]].map(([m, l]) => (
                <button key={m} data-testid={`mode-${m}`} onClick={() => { setMode(m); setQty(1); }} className={`px-4 h-9 rounded-full text-sm font-semibold ${mode === m ? "bg-neutral-900 text-white" : ""}`}>{l}</button>
              ))}
            </div>
          )}

          <div className="mt-5" data-testid="product-price">
            {mode === "po" && p.po_info ? (
              <div className="price text-3xl font-extrabold">{rp(p.po_info.price)} <span className="text-sm font-semibold text-neutral-500">harga PO</span></div>
            ) : (
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className={`price text-3xl font-extrabold ${pr.discount_pct ? "text-[var(--bk-red)]" : ""}`}>{rp(pr.final_price)}</span>
                {pr.discount_pct > 0 && <span className="text-neutral-400 line-through">{rp(pr.price)}</span>}
                {pr.promo && <span className="text-xs font-semibold text-[var(--bk-red)]">{pr.promo.title} · s.d. {fmtDateTime(pr.promo.end_at)}</span>}
              </div>
            )}
          </div>

          {mode === "ready" && (
            <p className="mt-2 text-sm" data-testid="stock-info">
              {p.stock > 0 ? <>Stok tersedia: <b>{hasVariants && variant ? lineMax(p, "ready", variant) : p.stock}</b> pcs</> : <span className="text-[var(--bk-red)] font-semibold">Stok habis</span>}
            </p>
          )}

          {mode === "ready" && hasVariants && (
            <div className="mt-5">
              <div className="text-sm font-semibold mb-2">Pilih varian</div>
              <div className="flex flex-wrap gap-2">
                {p.variants.map((v) => (
                  <button key={v.name} disabled={v.stock < 1} data-testid={`variant-${v.name}`} onClick={() => { setVariant(v.name); setQty(1); }}
                    className={`px-4 h-10 rounded-lg border-2 text-sm font-semibold disabled:opacity-40 disabled:line-through ${variant === v.name ? "border-neutral-900 bg-[var(--bk-yellow)]" : "border-neutral-300"}`}>
                    {v.name} <span className="text-xs font-normal">({v.stock})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === "po" && p.po_info && <div className="mt-5"><PoPanel po={p.po_info} /></div>}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center rounded-full border-2 border-neutral-900 bg-white h-12">
              <button data-testid="qty-decrease" aria-label="Kurangi" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-full w-11 grid place-items-center"><Minus className="h-4 w-4" /></button>
              <span data-testid="qty-value" className="w-10 text-center font-bold">{qty}</span>
              <button data-testid="qty-increase" aria-label="Tambah" onClick={() => setQty((q) => Math.min(Math.max(1, max), q + 1))} className="h-full w-11 grid place-items-center"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="add-to-cart-button" disabled={!orderable || needVariant || qty > max} onClick={() => addToCart(p, mode, mode === "ready" ? variant : "", qty)} className="btn-brand flex-1 min-w-[180px]">
              <ShoppingCart className="h-4 w-4" />{needVariant ? "Pilih varian dulu" : orderable ? (mode === "po" ? "Pesan PO" : "Tambah ke Keranjang") : mode === "po" ? "PO tidak dibuka" : "Stok habis"}
            </button>
            <button data-testid="detail-fav-button" onClick={() => toggleFav(p.id)} aria-label="Favorit" className={`h-12 w-12 grid place-items-center rounded-full border-2 border-neutral-900 ${fav ? "bg-[var(--bk-red)] text-white" : "bg-white"}`}>
              <Heart className="h-5 w-5" fill={fav ? "currentColor" : "none"} />
            </button>
          </div>
          {waContacts(s).length > 1 ? (
            <div className="mt-3 space-y-2" data-testid="detail-wa-list">
              <div className="text-xs font-semibold text-neutral-600">Tanya via WhatsApp:</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {waContacts(s).map((c, i) => <a key={c.number} data-testid={`detail-wa-button-${i}`} href={waLink(c.number, waText)} target="_blank" rel="noreferrer" className="btn-wa w-full"><MessageCircle className="h-4 w-4" />{c.label}</a>)}
              </div>
            </div>
          ) : (
            <a data-testid="detail-wa-button" href={waLink(s.whatsapp, waText)} target="_blank" rel="noreferrer" className="btn-wa w-full mt-3"><MessageCircle className="h-4 w-4" />Tanya via WhatsApp</a>
          )}
          <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-neutral-600">
            <div className="flex gap-2 items-start"><Truck className="h-4 w-4 shrink-0 text-[var(--bk-orange)]" />Ambil di toko atau kirim (ongkir dikonfirmasi admin)</div>
            <div className="flex gap-2 items-start"><Wallet className="h-4 w-4 shrink-0 text-[var(--bk-orange)]" />Pembayaran diverifikasi manual oleh admin</div>
          </div>
        </div>
      </div>

      <div className="mt-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-xl font-bold mb-3">Deskripsi</h2>
          <p className="prose-text" data-testid="product-description">{p.description || "Belum ada deskripsi."}</p>
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold mb-3">Spesifikasi</h2>
          <div className="rounded-xl border bg-white p-4" data-testid="product-specs">
            <Row label="Merek">{p.brand || "-"}</Row>
            <Row label="Kategori">{p.category?.name || "-"}</Row>
            <Row label="SKU">{p.sku}</Row>
            {(p.specs || []).map((sp, i) => <Row key={i} label={sp.label}>{sp.value}</Row>)}
          </div>
        </div>
      </div>

      {mode === "po" && <div className="mt-12"><PoTermsBlock /></div>}

      {p.related?.length > 0 && (
        <div className="mt-14" data-testid="related-products">
          <h2 className="font-heading text-2xl font-extrabold mb-5">Produk Terkait</h2>
          <ProductGrid items={p.related.slice(0, 4)} />
        </div>
      )}
    </div>
  );
}
